import { useEffect, useState, useRef } from "react";
import {
  collection, getDocs, doc, updateDoc, addDoc, deleteDoc,
  query, orderBy
} from "firebase/firestore";
import { db, storage } from "./firebaseConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./index.css";

function Oradores() {
  const [oradores, setOradores] = useState([]);
  const [erro, setErro] = useState("");
  const [selecionado, setSelecionado] = useState(null);
  const [pesquisa, setPesquisa] = useState("");
  const [novo, setNovo] = useState({ nome: "", cargo: "", avatar: "" });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileRef = useRef(null);

  const fetchOradores = async () => {
    try {
      const q = query(collection(db, "evento_oradores"), orderBy("nome", "asc"));
      const snapshot = await getDocs(q);
      setOradores(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch {
      setErro("Erro ao carregar os oradores.");
    }
  };

  useEffect(() => { fetchOradores(); }, []);

  useEffect(() => {
    if (novo.avatar) {
      if (typeof novo.avatar === "string") setAvatarPreview(novo.avatar);
      else setAvatarPreview(URL.createObjectURL(novo.avatar));
    } else {
      setAvatarPreview(null);
    }
  }, [novo.avatar]);

  const salvarCampo = async (id, campo, valor) => {
    const docRef = doc(db, "evento_oradores", id);
    await updateDoc(docRef, { [campo]: valor });
  };

  const salvarAvatar = async (id) => {
    if (!novo.avatar || typeof novo.avatar === "string") return null;
    const avatarRef = ref(storage, `oradores/${novo.avatar.name}`);
    await uploadBytes(avatarRef, novo.avatar);
    const url = await getDownloadURL(avatarRef);
    await salvarCampo(id, "avatar", url);
    return url;
  };

  const deletarOrador = async (id) => {
    if (!window.confirm("Tem certeza de que deseja excluir este orador da app?")) return;
    await deleteDoc(doc(db, "evento_oradores", id));
    setOradores(prev => prev.filter(o => o.id !== id));
    if (selecionado?.id === id) setSelecionado(null);
  };

  const salvarTudo = async () => {
    if (!novo.nome.trim()) { alert("O campo 'Nome' é obrigatório."); return; }

    try {
      let id = selecionado?.id;
      let avatarURL = selecionado?.avatar || "";

      if (!id || id.startsWith("temp-")) {
        const novoDoc = { nome: novo.nome, cargo: novo.cargo, avatar: "" };
        const docRef = await addDoc(collection(db, "evento_oradores"), novoDoc);
        id = docRef.id;
      } else {
        await salvarCampo(id, "nome", novo.nome);
        await salvarCampo(id, "cargo", novo.cargo);
      }

      if (novo.avatar) avatarURL = await salvarAvatar(id) || avatarURL;

      await fetchOradores();
      setSelecionado(null);
      setNovo({ nome: "", cargo: "", avatar: "" });
      setAvatarPreview(null);
    } catch {
      alert("Erro ao guardar os dados.");
    }
  };

  const exportarCSV = () => {
    const headers = ["nome", "cargo", "avatar"];
    const rows = oradores.length
      ? oradores.map(d => headers.map(h => d[h] || ""))
      : [["", "", ""]];
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(";")).join("\n");
    const bom = "\uFEFF";
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "oradores.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const detectSeparator = (line) => (line.includes(";") ? ";" : ",");

  const handleImportarCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    window.alert("A sua importação está em processamento. Por favor, aguarde...");

    try {
      const text = await file.text();
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) throw new Error("ficheiro vazio");

      const sep = detectSeparator(lines[0]);
      const headers = lines[0].split(sep).map(h => h.replace(/"/g, "").trim());
      if (!headers.includes("nome") || !headers.includes("cargo")) throw new Error("estrutura inválida");

      const nomesExistentes = oradores.map(o => o.nome.toLowerCase().trim());
      const duplicados = [];
      let adicionados = 0;
      const novos = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(sep).map(v => v.replace(/"/g, "").trim());
        const obj = {};
        headers.forEach((h, j) => { obj[h] = values[j] || ""; });

        const nomeLower = (obj.nome || "").toLowerCase().trim();
        if (!nomeLower) continue;

        if (nomesExistentes.includes(nomeLower)) duplicados.push(obj.nome);
        else { nomesExistentes.push(nomeLower); novos.push(obj); }
      }

      if (novos.length === 0 && duplicados.length > 0) {
        window.alert("Todos os dados já estão inseridos na tabela");
        e.target.value = null;
        return;
      }

      for (const obj of novos) { await addDoc(collection(db, "evento_oradores"), obj); adicionados++; }
      await fetchOradores();

      if (duplicados.length > 0) {
        window.alert(
          `A sua importação foi concluída parcialmente.\n` +
          `Foram inseridos ${adicionados} dados.\n` +
          `Dados duplicados: ${duplicados.join(", ")}`
        );
      } else {
        window.alert(`A sua importação foi concluída. Foram inseridos ${adicionados} dados.`);
      }
    } catch (err) {
      console.error(err);
      window.alert("A sua importação falhou. Verifique o CSV.");
    }

    e.target.value = null;
  };

  const oradoresFiltrados = oradores
    .filter(o => o.nome?.toLowerCase().includes(pesquisa.toLowerCase()))
    .sort((a, b) => a.nome.localeCompare(b.nome));

  if (erro) return <p>{erro}</p>;

  return (
    <div className="table-container">
      <div>
        <input
          type="text"
          placeholder="Pesquisar por nome"
          value={pesquisa}
          onChange={e => setPesquisa(e.target.value)}
          className="input"
        />
        <button className="btn btn-other" onClick={exportarCSV}>Exportar CSV</button>
        <label className="btn btn-other">
          Importar CSV
          <input type="file" accept=".csv" style={{ display: "none" }} onChange={handleImportarCSV} />
        </label>
      </div>

      <div>
        {oradoresFiltrados.length === 0 ? (
          <p style={{ textAlign: "center", color: "#292f2c" }}>Sem dados</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Avatar</th>
                <th>Nome</th>
                <th>Cargo</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {oradoresFiltrados.map(o => (
                <tr key={o.id}>
                  <td
                    style={{ cursor: "pointer" }}
                    title="Clique para copiar o ID"
                    onClick={() => navigator.clipboard.writeText(o.id)}
                  >
                    {o.id}
                  </td>
                  <td>
                    {o.avatar ? (
                      <img
                        src={o.avatar}
                        alt="Avatar"
                        style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover" }}
                      />
                    ) : "—"}
                  </td>
                  <td>{o.nome || "—"}</td>
                  <td>{o.cargo || "—"}</td>
                  <td>
                    <button
                      className="btn btn-add"
                      onClick={() => {
                        setSelecionado(o);
                        setNovo({ nome: o.nome, cargo: o.cargo, avatar: o.avatar || "" });
                      }}
                    >
                      Editar
                    </button>

                    <button className="btn btn-delete" onClick={() => deletarOrador(o.id)}>
                      Apagar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selecionado && (
        <div className="drawer">
          <div className="drawer-content">
            <label>Nome:</label>
            <input
              type="text"
              value={novo.nome}
              onChange={e => setNovo(prev => ({ ...prev, nome: e.target.value }))}
            />

            <br /><br />
            <label>Cargo:</label>
            <input
              type="text"
              value={novo.cargo}
              onChange={e => setNovo(prev => ({ ...prev, cargo: e.target.value }))}
            />

            <br /><br />
            <label>Avatar:</label>
            {avatarPreview && (
              <img
                src={avatarPreview}
                alt="Avatar"
                style={{ width: 100, height: 100, borderRadius: "50%", objectFit: "cover" }}
              />
            )}
            <input
              type="file"
              ref={fileRef}
              onChange={e => {
                const file = e.target.files[0];
                setNovo(prev => ({ ...prev, avatar: file || "" }));
              }}
            />
          </div>

          <div className="drawer-footer">
            <button className="btn btn-add" onClick={salvarTudo}>Guardar</button>
            <button
              className="btn btn-cancel"
              onClick={() => {
                setSelecionado(null);
                setNovo({ nome: "", cargo: "", avatar: "" });
                setAvatarPreview(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Oradores;
